import { useEffect } from 'react';
import { useFieldState, useFormApi } from 'informed';
import { useReduxState } from 'hooks';
import { FormGroup, NumberStepper } from 'components/common/form';

interface Props {
  hasSavedLimits: boolean;
  isRequired: boolean;
  numberOfOptions: number;
}

const maxField = 'numberOfAllowedSelectionsMax';
const minField = 'numberOfAllowedSelectionsMin';

/**
 * The selection limits of a multiple choice question are bounded by how many
 * options it has and by whether it has to be answered, and both bounds move
 * while the question is being edited. Each limit follows its bound until the
 * user sets it themselves, and is then only moved when it falls out of bounds.
 *
 * Single choice questions have a single valid value for each limit, so they
 * render no steppers at all and their values are derived on save instead.
 */
const CalendarEventFeedbackQuestionSelectionLimits = ({
  hasSavedLimits,
  isRequired,
  numberOfOptions
}: Props) => {
  const { messages } = useReduxState();

  const formApi = useFormApi();

  // the maximum is a bound of the minimum, so changes to it have to be followed
  const { value: maxSelections } = useFieldState<number>(maxField);

  const smallestMin = isRequired ? 1 : 0;

  useEffect(() => {
    // a choice question always has at least one option, so none of them being
    // there yet means the form state has not caught up with its initial values
    // and both limits would be clamped against a bound that is about to change
    if (!numberOfOptions) return;

    // a limit is the user's own once they set it, so even a hard reload
    // or editing the question should not reset them to their bounds
    const isUserDefined = (field: string) =>
      hasSavedLimits || formApi.getTouched(field);

    // setValue does not mark a field as touched, so syncing here does not count
    // as the user taking over either field and we just ensure the maximum is
    // either a user defined value or the maximum number of options
    const currentMax = formApi.getValue<number>(maxField);
    const currentMin = formApi.getValue<number>(minField);

    const nextMax = isUserDefined(maxField)
      ? Math.min(Math.max(currentMax, 1), numberOfOptions)
      : numberOfOptions;
    if (nextMax !== currentMax) formApi.setValue(maxField, nextMax);

    const nextMin = isUserDefined(minField)
      ? Math.min(Math.max(currentMin, smallestMin), nextMax)
      : smallestMin;
    if (nextMin !== currentMin) formApi.setValue(minField, nextMin);
  }, [formApi, hasSavedLimits, maxSelections, numberOfOptions, smallestMin]);

  return (
    <>
      <FormGroup
        htmlFor="feedback-question-min-selections"
        label={messages.eventCalendarFeedbackMinSelections}
      >
        <NumberStepper
          field={minField}
          id="feedback-question-min-selections"
          max={maxSelections}
          min={smallestMin}
          stepValue={1}
        />
      </FormGroup>

      <FormGroup
        htmlFor="feedback-question-max-selections"
        label={messages.eventCalendarFeedbackMaxSelections}
      >
        <NumberStepper
          field={maxField}
          id="feedback-question-max-selections"
          max={numberOfOptions}
          min={1}
          stepValue={1}
        />
      </FormGroup>
    </>
  );
};

export default CalendarEventFeedbackQuestionSelectionLimits;
